/**
 * クライアント側の画像縮小・再エンコード（docs/02-architecture.md §9、ADR-009）。
 *
 * サーバー（Workers 無料枠）の CPU 制限を避けるため、変換はすべてここで行う。
 * canvas への描き直しは EXIF（撮影日時・GPS 位置情報）を副次的に落とすので、
 * プライバシー面でも望ましい。元画像（フル解像度）は保持しない。
 */

/** 長辺の上限。 */
export const MAX_EDGE = 2048;
/** 写真の JPEG 品質。 */
export const JPEG_QUALITY = 0.8;
/** アップロードの上限（Worker 側でも検証する）。 */
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

export interface PreparedImage {
  blob: Blob;
  width: number;
  height: number;
  /** 拡張子を含むファイル名の候補。 */
  filename: string;
}

/** 画像として扱う MIME。 */
const SUPPORTED = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

export function isSupportedImage(file: File | Blob): boolean {
  return SUPPORTED.has(file.type);
}

/**
 * アップロード用に画像を整える。
 *
 * - 長辺が MAX_EDGE を超えていれば縮小する
 * - PNG は MAX_EDGE 以下ならそのまま維持する（スクリーンショットの文字を
 *   JPEG のブロックノイズで潰さないため）
 * - GIF はアニメーションを壊さないよう変換しない。上限超過なら拒否する
 * - それ以外は JPEG 品質 0.8 で再エンコードする
 */
export async function prepareImageForUpload(file: File): Promise<PreparedImage> {
  if (!isSupportedImage(file)) {
    throw new Error('対応していない画像形式です');
  }

  if (file.type === 'image/gif') {
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error('GIF が大きすぎます（8MB まで）');
    }
    const size = await readImageSize(file);
    return { blob: file, width: size.width, height: size.height, filename: file.name || 'image.gif' };
  }

  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const keepPng = file.type === 'image/png' && scale === 1;

    if (keepPng && file.size <= MAX_UPLOAD_BYTES) {
      return {
        blob: file,
        width: bitmap.width,
        height: bitmap.height,
        filename: file.name || 'image.png',
      };
    }

    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas を初期化できませんでした');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);

    // 縮小後も PNG のままにできるのは、元が PNG で縮小だけした場合。
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await canvasToBlob(canvas, type, JPEG_QUALITY);

    if (blob.size > MAX_UPLOAD_BYTES) {
      // PNG のまま上限を超えるなら JPEG へ落とす（スクリーンショットでも稀に起きる）。
      const fallback = await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY);
      if (fallback.size > MAX_UPLOAD_BYTES) {
        throw new Error('画像が大きすぎます（8MB まで）');
      }
      return { blob: fallback, width, height, filename: renameExt(file.name, 'jpg') };
    }

    return {
      blob,
      width,
      height,
      filename: renameExt(file.name, type === 'image/png' ? 'png' : 'jpg'),
    };
  } finally {
    bitmap.close();
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('画像の書き出しに失敗しました'))),
      type,
      quality,
    );
  });
}

async function readImageSize(file: Blob): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const size = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  return size;
}

function renameExt(name: string, ext: string): string {
  const base = (name || 'image').replace(/\.[^.]+$/, '');
  return `${base}.${ext}`;
}

/** ペーストイベントから画像ファイルを拾う（PC・スマホ共通の貼り付け動線）。 */
export function imageFilesFromClipboard(event: ClipboardEvent): File[] {
  const items = event.clipboardData?.items;
  if (!items) return [];
  const files: File[] = [];
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (file && isSupportedImage(file)) files.push(file);
  }
  return files;
}
